import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, Phone, Mail, UserCircle } from 'lucide-react';
import { ImageUpload } from '../ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function ContactManager() {
  const [contacts, setContacts] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'CONTACT US', hero_subtitle_bg: 'СВЪРЖЕТЕ СЕ С НАС', hero_subtitle_de: 'KONTAKTIEREN SIE UNS',
    hero_title_en: 'Get in', hero_title_bg: 'Свържете се', hero_title_de: 'Nehmen Sie',
    hero_title_highlight_en: 'Touch', hero_title_highlight_bg: 'с нас', hero_title_highlight_de: 'Kontakt auf',
    hero_description_en: 'Have questions? Our team is here to help you with all your transport needs.',
    hero_description_bg: 'Имате въпроси? Нашият екип е тук да ви помогне с всички ваши транспортни нужди.',
    hero_description_de: 'Haben Sie Fragen? Unser Team hilft Ihnen gerne bei all Ihren Transportbedürfnissen.',
    form_title_en: 'Send us a message', form_title_bg: 'Изпратете ни съобщение', form_title_de: 'Senden Sie uns eine Nachricht',
    form_name_en: 'Your Name', form_name_bg: 'Вашето име', form_name_de: 'Ihr Name',
    form_email_en: 'Email Address', form_email_bg: 'Имейл адрес', form_email_de: 'E-Mail-Adresse',
    form_phone_en: 'Phone Number', form_phone_bg: 'Телефонен номер', form_phone_de: 'Telefonnummer',
    form_message_en: 'Your Message', form_message_bg: 'Вашето съобщение', form_message_de: 'Ihre Nachricht',
    form_submit_en: 'Send Message', form_submit_bg: 'Изпрати съобщение', form_submit_de: 'Nachricht senden'
  });
  
  const defaultForm = {
    name: '', name_en: '', name_bg: '', name_de: '',
    position_en: '', position_bg: '', position_de: '',
    job_title_en: '', job_title_bg: '', job_title_de: '',
    email: '', phone: '', image_url: '',
    order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchContacts(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/contact-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  // Normalize contact data to handle both old (position_) and new (job_title_) field names
  const normalizeContact = (contact) => ({
    ...contact,
    name_en: contact.name_en || contact.name || '',
    name_bg: contact.name_bg || contact.name || '',
    name_de: contact.name_de || '',
    position_en: contact.position_en || contact.job_title_en || '',
    position_bg: contact.position_bg || contact.job_title_bg || '',
    position_de: contact.position_de || contact.job_title_de || '',
  });

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/contact-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact-persons`);
      // API returns 'persons' not 'contacts'
      const contactList = response.data.persons || response.data.contacts || [];
      setContacts(contactList.map(normalizeContact));
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API}/admin/contact-persons/${editingContact.id}`, formData);
        toast.success('Contact updated!');
      } else {
        await axios.post(`${API}/admin/contact-persons`, formData);
        toast.success('Contact added!');
      }
      fetchContacts();
      setIsFormOpen(false);
      setEditingContact(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving contact');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contact-persons/${id}`);
      toast.success('Contact deleted!');
      fetchContacts();
    } catch (error) {
      toast.error('Error deleting contact');
    }
  };

  const startEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name || '',
      name_en: contact.name_en || contact.name || '',
      name_bg: contact.name_bg || contact.name || '',
      name_de: contact.name_de || '',
      position_en: contact.position_en || contact.job_title_en || '',
      position_bg: contact.position_bg || contact.job_title_bg || '',
      position_de: contact.position_de || contact.job_title_de || '',
      email: contact.email || '',
      phone: contact.phone || '',
      image_url: contact.image_url || contact.image || '',
      order: contact.order || 0,
      is_active: contact.is_active !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const updatePageField = (field, value) => setPageContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Contact Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} Hero Section</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} placeholder="Subtitle" className="bg-white" />
                        <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} placeholder="Title" className="bg-white" />
                      </div>
                      <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} placeholder="Title Highlight" className="bg-white" />
                      <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} placeholder="Description" className="bg-white" rows={2} />
                      
                      <h4 className={`font-bold ${lang.text} pt-2`}>{lang.flag} Form Labels</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`form_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`form_title_${lang.code}`, e.target.value)} placeholder="Form Title" className="bg-white" />
                        <Input value={pageContent[`form_name_${lang.code}`] || ''} onChange={(e) => updatePageField(`form_name_${lang.code}`, e.target.value)} placeholder="Name Label" className="bg-white" />
                        <Input value={pageContent[`form_email_${lang.code}`] || ''} onChange={(e) => updatePageField(`form_email_${lang.code}`, e.target.value)} placeholder="Email Label" className="bg-white" />
                        <Input value={pageContent[`form_phone_${lang.code}`] || ''} onChange={(e) => updatePageField(`form_phone_${lang.code}`, e.target.value)} placeholder="Phone Label" className="bg-white" />
                        <Input value={pageContent[`form_message_${lang.code}`] || ''} onChange={(e) => updatePageField(`form_message_${lang.code}`, e.target.value)} placeholder="Message Label" className="bg-white" />
                        <Input value={pageContent[`form_submit_${lang.code}`] || ''} onChange={(e) => updatePageField(`form_submit_${lang.code}`, e.target.value)} placeholder="Submit Button" className="bg-white" />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Contact Persons</h2>
          <p className="text-sm text-slate-500">Team members shown on contact page</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingContact(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Contact
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingContact ? 'Edit Contact' : 'New Contact'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name - Language Tabs */}
              <div>
                <Label className="mb-2 block">Name (Translations) *</Label>
                <Tabs value={activeLang} onValueChange={setActiveLang}>
                  <TabsList className="grid w-full grid-cols-3 mb-2">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                        {lang.flag} {formData[`name_${lang.code}`] && '✓'}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code}>
                      <Input 
                        value={formData[`name_${lang.code}`] || ''} 
                        onChange={(e) => updateField(`name_${lang.code}`, e.target.value)} 
                        placeholder={`Full Name (${lang.name})`} 
                        required={lang.code === 'en'}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} placeholder="email@example.com" className="mt-1" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+359 888 123 456" className="mt-1" />
                </div>
              </div>

              {/* Job Title - Language Tabs */}
              <div>
                <Label className="mb-2 block">Job Title / Position (Translations)</Label>
                <Tabs value={activeLang} onValueChange={setActiveLang}>
                  <TabsList className="grid w-full grid-cols-3 mb-2">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                        {lang.flag} {formData[`position_${lang.code}`] && '✓'}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code}>
                      <div className={`p-3 rounded-xl ${lang.bg}`}>
                        <Input 
                          value={formData[`position_${lang.code}`]} 
                          onChange={(e) => updateField(`position_${lang.code}`, e.target.value)} 
                          placeholder={lang.code === 'en' ? 'e.g., Managing Director' : lang.code === 'bg' ? 'напр., Управляващ директор' : 'z.B., Geschäftsführer'}
                          className="bg-white"
                          required={lang.code === 'en'}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              {/* Image */}
              <div>
                <Label>Photo</Label>
                <div className="mt-1">
                  <ImageUpload value={formData.image_url} onChange={(url) => updateField('image_url', url)} placeholder="Upload photo" />
                </div>
              </div>

              {/* Order & Active */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                <div>
                  <Label>Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => updateField('order', parseInt(e.target.value) || 0)} className="mt-1" />
                </div>
                <label className="flex items-center gap-2 h-10">
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} />
                  <span>Active</span>
                </label>
              </div>

              {/* Actions - sticky at bottom */}
              <div className="flex justify-end gap-2 pt-4 border-t sticky bottom-0 bg-white pb-2">
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A] hover:bg-[#A01830]"><Save className="w-4 h-4 mr-2" /> Save Contact</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Contacts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contacts.map(contact => (
          <Card key={contact.id} className={`bg-white rounded-2xl shadow-sm ${!contact.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {(contact.image_url || contact.image) ? (
                  <img src={contact.image_url || contact.image} alt={contact.name} className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                    <UserCircle className="w-8 h-8 text-[#C41E3A]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold truncate">{contact.name_en || contact.name_bg || contact.name || 'No name'}</h4>
                  <p className="text-sm text-slate-500">{contact.position_en || contact.job_title_en || ''}</p>
                  <div className="flex gap-1 mt-1">
                    {LANGUAGES.map(lang => (
                      <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${(contact[`position_${lang.code}`] || contact[`job_title_${lang.code}`] || contact[`name_${lang.code}`]) ? lang.bg : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t text-sm text-slate-500">
                {contact.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {contact.email}</span>}
              </div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(contact)} className="flex-1"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(contact.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {contacts.length === 0 && <p className="text-slate-500 text-center py-8 col-span-3">No contacts yet.</p>}
      </div>
    </div>
  );
}
